/* eslint-disable react-hooks/rules-of-hooks */
import { BoxFile } from "@modules/box/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDocVaultJwt, useFileDownloadURL, useFolder } from "./queries";
import { useStore, usePersistStore } from "./state";
import { useHousehold } from "@common/queries";
import { getHouseholdAppUserName } from "@modules/box/client";

const usePrint = ({ file }: { file: BoxFile }) => {
  const { refetch: getFileDownloadURL } = useFileDownloadURL({ file });

  const print = async () => {
    const { data } = await getFileDownloadURL();
    const url = data?.url;
    const printer = await import("print-js");
    printer.default({
      printable: url,
      type: file.extension === "pdf" ? "pdf" : "image",
    });
  };
  const canPrint = ["pdf", "jpeg", "jpg", "png", "gif"].includes(
    file.extension ?? ""
  );

  return { print, canPrint };
};

const useDownload = ({
  file,
  isMobile,
}: {
  file: BoxFile;
  isMobile?: boolean;
}) => {
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const { refetch: getFileDownloadURL } = useFileDownloadURL({ file });
  const download = useCallback(async () => {
    setIsDownloading(true);
    try {
      const res = await getFileDownloadURL();
      if (res.data?.url) {
        if (isMobile) {
          return res.data.url;
        } else {
          window.location.href = res.data.url;
        }
      }
    } catch (error) {
    } finally {
      setIsDownloading(false);
    }
  }, [getFileDownloadURL, isMobile]);
  return { download, isDownloading };
};

const usePrivateFolderId = () => {
  const { boxPrivateFolderId, setBoxPrivateFolderId } = usePersistStore();

  const { data: apiToken } = useDocVaultJwt();
  useEffect(() => {
    if (apiToken?.privateRootFolderId) {
      setBoxPrivateFolderId(apiToken?.privateRootFolderId);
    }
  }, [apiToken?.privateRootFolderId, setBoxPrivateFolderId]);
  return boxPrivateFolderId;
};

const useRootFolderId = () => {
  const { boxRootFolderId, setBoxRootFolderId } = usePersistStore();

  const { data: apiToken } = useDocVaultJwt();
  useEffect(() => {
    if (apiToken?.rootFolderId) {
      setBoxRootFolderId(apiToken?.rootFolderId);
    }
  }, [apiToken?.rootFolderId, setBoxRootFolderId]);
  return boxRootFolderId;
};

const useCurrentFolder = () => {
  const rootFolderId = useRootFolderId();
  const currentFolderId =
    useStore((state) => state.currentFolderId) || rootFolderId;
  const setCurrentFolderId = useStore((state) => state.setCurrentFolderId);

  const { data: apiToken } = useDocVaultJwt();
  const accessToken = apiToken?.accessToken ?? "";

  const { data: currentFolder, isLoading } = useFolder({
    accessToken,
    folderId: currentFolderId,
  });

  return {
    currentFolder,
    currentFolderId,
    setCurrentFolderId,
    isLoading,
  };
};

const useRootFolder = (isMobile = false) => {
  const rootFolderId = useRootFolderId();

  const { data: apiToken } = useDocVaultJwt();
  const accessToken = apiToken?.accessToken ?? "";

  const {
    data: rootFolder,
    isLoading,
    refetch,
  } = useFolder({
    accessToken,
    folderId: rootFolderId,
  });

  return {
    rootFolder,
    isLoading,
    refetch,
  };
};

const usePrivateFolder = () => {
  const privateFolderId = usePrivateFolderId();

  const { data: apiToken } = useDocVaultJwt();
  const accessToken = apiToken?.accessToken ?? "";

  const { data: folderZeroContents, isLoading } = useFolder({
    accessToken,
    folderId: privateFolderId,
  });
  const privateFolder = folderZeroContents?.folders.find((f) =>
    f.name.match(/private/i)
  );
  return {
    privateFolder,
    isLoading,
  };
};

const useFolderByName = (folderName: string) => {
  const rootFolderId = useRootFolderId();

  const { data: apiToken } = useDocVaultJwt();
  const accessToken = apiToken?.accessToken ?? "";

  const { data: folderZeroContents } = useFolder({
    accessToken,
    folderId: rootFolderId,
  });
  const regex = new RegExp(folderName);
  const matchingFolder = folderZeroContents?.folders.find((f) =>
    f.name.match(regex)
  );
  return matchingFolder;
};

const useHouseholdOwnerName = () => {
  const { data: household } = useHousehold();
  return useMemo(
    () => household && getHouseholdAppUserName(household),
    [household]
  );
};

const useDecryptPdfDocument = <T>(): { decrypt: (file: T) => Promise<T> } => {
  const {
    setFileToDecrypt,
    setShowFileDecryptModal,
    setFileDecryptDialogCloseHandler,
    setFileDecryptDialogSubmitHandler,
  } = useStore();
  const decrypt: (file: T) => Promise<T> = (file) =>
    new Promise((resolve, reject) => {
      setFileDecryptDialogCloseHandler(() => {
        setShowFileDecryptModal(false);
        reject(null);
        setFileDecryptDialogSubmitHandler(undefined);
        setFileDecryptDialogCloseHandler(undefined);
      });
      setFileDecryptDialogSubmitHandler<T>((file) => {
        setShowFileDecryptModal(false);
        resolve(file);
        setFileDecryptDialogSubmitHandler(undefined);
        setFileDecryptDialogCloseHandler(undefined);
      });
      setFileToDecrypt<T>(file);
      setShowFileDecryptModal(true);
    });
  return { decrypt };
};

export {
  usePrint,
  useDownload,
  useCurrentFolder,
  useRootFolder,
  useRootFolderId,
  usePrivateFolder,
  useFolderByName,
  useHouseholdOwnerName,
  useDecryptPdfDocument,
};
